import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { productsAPI } from '@/shared/services/api';
import { SortableHeader, useSortableData } from '@/shared/components/ui/sortable-table';
import {
  Search,
  Plus,
  Package,
  Barcode,
  QrCode,
  Edit,
  Trash2,
  MoreHorizontal,
  Box,
  Ruler,
  Scale,
  Download,
  Upload,
  Loader2,
  LayoutGrid,
  List,
  Eye } from
'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger } from
'@/shared/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import { toast } from 'sonner';

// View preference key for localStorage
const VIEW_PREFERENCE_KEY = 'ezerp_products_view_mode';

export const Products = () => {const { t } = useTranslation("wms");
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, categories: 0, withBarcodes: 0, coldChain: 0 });

  // View mode state with persistence
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem(VIEW_PREFERENCE_KEY) || 'cards';
  });

  const [newProduct, setNewProduct] = useState({
    sku: '', name: '', barcode: '', category: '',
    dimensions_width: '', dimensions_height: '', dimensions_length: '',
    weight_value: '', box_qty: '', shipping_config: 'Standard', handling_notes: ''
  });

  // Persist view preference
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_PREFERENCE_KEY, mode);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [productsData, statsData] = await Promise.all([
        productsAPI.getProducts(),
        productsAPI.getStats()]
        );
        setProducts(productsData || []);
        setStats({
          total: statsData?.total_products || productsData?.length || 0,
          categories: statsData?.categories || 2,
          withBarcodes: statsData?.with_barcodes || productsData?.filter((p) => p.barcode).length || 0,
          coldChain: statsData?.cold_chain || productsData?.filter((p) => p.shippingConfig === 'Cold Chain').length || 0
        });
      } catch (error) {
        console.error('Failed to fetch products:', error);
        toast.error('Failed to load products');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Apply sorting to products
  const { sortedData, sortConfig, requestSort } = useSortableData(products);

  const filteredProducts = (sortedData || []).filter((product) => {
    const name = product.name || '';
    const sku = product.sku || '';
    return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sku.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleCreateProduct = async () => {
    try {
      await productsAPI.createProduct(newProduct);
      toast.success('Product created successfully');
      setShowCreateDialog(false);
      // Refresh products
      const productsData = await productsAPI.getProducts();
      setProducts(productsData || []);
      setNewProduct({
        sku: '', name: '', barcode: '', category: '',
        dimensions_width: '', dimensions_height: '', dimensions_length: '',
        weight_value: '', box_qty: '', shipping_config: 'Standard', handling_notes: ''
      });
    } catch (error) {
      console.error('Failed to create product:', error);
      toast.error('Failed to create product');
    }
  };

  return (
    <div className="space-y-6" data-testid="wms-products">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("products", "Products")}</h1>
          <p className="text-muted-foreground mt-1">{t("manageProductMaster", "Manage product master data and configurations")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2">
            <Upload className="h-4 w-4" />{t("import", "Import")}

          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />{t("export", "Export")}

          </Button>
          <Button
            className="gap-2 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]"
            onClick={() => setShowCreateDialog(true)}>
            
            <Plus className="h-4 w-4" />{t("addProduct", "Add Product")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="card-elevated">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("totalProducts", "Total Products")}</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
            <Package className="h-8 w-8 text-muted-foreground/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("categories", "Categories")}</p>
              <p className="text-2xl font-bold">{stats.categories}</p>
            </div>
            <Box className="h-8 w-8 text-muted-foreground/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("withBarcodes", "With Barcodes")}</p>
              <p className="text-2xl font-bold">{stats.withBarcodes}</p>
            </div>
            <Barcode className="h-8 w-8 text-muted-foreground/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("coldChain", "Cold Chain")}</p>
              <p className="text-2xl font-bold">{stats.coldChain}</p>
            </div>
            <Box className="h-8 w-8 text-blue-500/30" />
          </CardContent>
        </Card>
      </div>

      {/* Search & Filters */}
      <Card className="card-elevated">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by SKU, product name, or barcode..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
            
            {/* View Toggle */}
            <div className="flex items-center border rounded-lg p-1 bg-muted/30">
              <Button
                variant={viewMode === 'cards' ? 'default' : 'ghost'}
                size="sm"
                className="gap-2"
                onClick={() => handleViewModeChange('cards')}
                data-testid="view-cards-btn">
                
                <LayoutGrid className="h-4 w-4" />{t("cards", "Cards")}

              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                className="gap-2"
                onClick={() => handleViewModeChange('list')}
                data-testid="view-list-btn">
                
                <List className="h-4 w-4" />{t("list", "List")}

              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Products View */}
      {loading ?
      <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">{t("loadingProducts", "Loading products...")}</span>
        </div> :
      filteredProducts.length === 0 ?
      <div className="text-center py-12">
          <Package className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">{t("noProductsFound", "No products found")}</p>
        </div> :
      viewMode === 'list' ? (
      /* List View */
      <Card className="card-elevated">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <SortableHeader column="sku" label="SKU" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableHeader column="name" label="Product Name" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableHeader column="category" label="Category" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableHeader column="barcode" label="Barcode" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableHeader column="dimensions" label="Dimensions" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableHeader column="weight" label="Weight" sortConfig={sortConfig} onSort={requestSort} align="right" />
                    <SortableHeader column="boxQty" label="Box Qty" sortConfig={sortConfig} onSort={requestSort} align="right" />
                    <SortableHeader column="shippingConfig" label="Shipping" sortConfig={sortConfig} onSort={requestSort} />
                    <th className="py-3 px-4 text-right font-medium">{t("actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredProducts.map((product) => {
                  const productId = product.id || product.sku;
                  const dims = product.dimensions || { width: 10, height: 10, length: 15, unit: 'cm' };
                  const weight = product.weight || { value: 0.5, unit: 'kg' };
                  return (
                    <tr key={productId} className="hover:bg-muted/30 transition-colors">
                        <td className="py-3 px-4">
                          <span className="font-mono text-sm">{product.sku}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-medium">{product.name}</span>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline">{product.category || 'General'}</Badge>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-mono text-sm text-muted-foreground">
                            {product.barcode || 'N/A'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                            <Ruler className="h-3.5 w-3.5" />
                            <span>{dims.width}x{dims.height}x{dims.length} {dims.unit}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1.5 text-sm">
                            <Scale className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>{weight.value} {weight.unit}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right text-sm">
                          {product.boxQty || 12}
                        </td>
                        <td className="py-3 px-4">
                          {product.shippingConfig === 'Cold Chain' ?
                        <Badge className="bg-blue-100 text-blue-700 border-blue-200">{t("coldChain", "Cold Chain")}</Badge> :

                        <Badge variant="secondary">{t("standard", "Standard")}</Badge>
                        }
                        </td>
                        <td className="py-3 px-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem className="gap-2">
                                <Eye className="h-4 w-4" />{t("viewDetails", "View Details")}

                            </DropdownMenuItem>
                              <DropdownMenuItem className="gap-2">
                                <Edit className="h-4 w-4" />{t("edit", "Edit")}

                            </DropdownMenuItem>
                              <DropdownMenuItem className="gap-2">
                                <Barcode className="h-4 w-4" />{t("printBarcode", "Print Barcode")}

                            </DropdownMenuItem>
                              <DropdownMenuItem className="gap-2 text-red-600">
                                <Trash2 className="h-4 w-4" />{t("delete", "Delete")}

                            </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>);

                })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>) : (

      /* Cards View */
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredProducts.map((product) => {
          const productId = product.id || product.sku;
          const dims = product.dimensions || { width: 10, height: 10, length: 15, unit: 'cm' };
          const weight = product.weight || { value: 0.5, unit: 'kg' };
          return (
            <Card key={productId} className="card-elevated hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <Badge variant="outline" className="mb-2">{product.category || 'General'}</Badge>
                      <h3 className="font-semibold text-sm line-clamp-2">{product.name}</h3>
                      <p className="text-xs text-muted-foreground font-mono mt-1">{product.sku}</p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem className="gap-2">
                          <Edit className="h-4 w-4" />{t("edit", "Edit")}

                      </DropdownMenuItem>
                        <DropdownMenuItem className="gap-2">
                          <Barcode className="h-4 w-4" />{t("printBarcode", "Print Barcode")}

                      </DropdownMenuItem>
                        <DropdownMenuItem className="gap-2 text-red-600">
                          <Trash2 className="h-4 w-4" />{t("delete", "Delete")}

                      </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Ruler className="h-4 w-4" />
                        <span>{dims.width}x{dims.height}x{dims.length} {dims.unit}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Scale className="h-4 w-4" />
                        <span>{weight.value} {weight.unit}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Barcode className="h-3.5 w-3.5" />
                        <span className="font-mono">{product.barcode || 'N/A'}</span>
                      </div>
                    </div>

                    {product.shippingConfig === 'Cold Chain' &&
                  <Badge className="bg-blue-100 text-blue-700 border-blue-200">{t("coldChainRequired", "Cold Chain Required")}

                  </Badge>
                  }

                    <div className="pt-2 border-t border-border">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{t("boxQty", "Box Qty:")}{product.boxQty || 12}</span>
                        <span>{t("palletQty", "Pallet Qty:")}{product.palletQty || 48}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>);

        })}
        </div>)
      }

      {/* Create Product Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("addNewProduct", "Add New Product")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium mb-2 block">{t("sku", "SKU *")}</label>
                <Input placeholder="Enter SKU code" />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">{t("barcode", "Barcode")}</label>
                <Input placeholder="Enter barcode" />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">{t("productName", "Product Name *")}</label>
              <Input placeholder="Enter product name" />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">{t("description", "Description")}</label>
              <Input placeholder="Enter product description" />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium mb-2 block">{t("category", "Category")}</label>
                <select className="w-full h-10 px-3 border border-input rounded-md bg-background">
                  <option value="">{t("selectCategory", "Select category")}</option>
                  <option value="pharmaceuticals">{t("pharmaceuticals", "Pharmaceuticals")}</option>
                  <option value="medical">{t("medicalSupplies", "Medical Supplies")}</option>
                  <option value="equipment">{t("equipment", "Equipment")}</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">{t("shippingConfig", "Shipping Config")}</label>
                <select className="w-full h-10 px-3 border border-input rounded-md bg-background">
                  <option value="standard">{t("standard", "Standard")}</option>
                  <option value="cold">{t("coldChain", "Cold Chain")}</option>
                  <option value="hazmat">{t("hazardous", "Hazardous")}</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">{t("dimensionsCm", "Dimensions (cm)")}</label>
              <div className="grid grid-cols-3 gap-3">
                <Input placeholder="Width" type="number" />
                <Input placeholder="Height" type="number" />
                <Input placeholder="Length" type="number" />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium mb-2 block">{t("weightKg", "Weight (kg)")}</label>
                <Input placeholder="Enter weight" type="number" step="0.01" />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">{t("boxQuantity", "Box Quantity")}</label>
                <Input placeholder="Units per box" type="number" />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">{t("handlingInstructions", "Handling Instructions")}</label>
              <Input placeholder="Special handling notes" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button>{t("createProduct", "Create Product")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};